import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { AspectRatio } from './ui/aspect-ratio';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const CoverCarousel = ({ covers = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  if (!covers || covers.length === 0) {
    return null;
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % covers.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + covers.length) % covers.length);
  };

  // Handle touch events for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrev();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const currentCover = covers[currentIndex];

  return (
    <div 
      data-testid="hero-cover-carousel" 
      className="relative w-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Link to="/covers" className="block" data-testid="cover-slide">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className="group relative overflow-hidden rounded-lg border border-border shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-300 cursor-pointer"
          >
            <AspectRatio ratio={3/4}>
              <img
                src={currentCover.coverImageUrl}
                alt={currentCover.title || `Issue ${currentCover.issueNumber}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </AspectRatio>
            
            {/* Issue info overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 pointer-events-none">
              <p className="text-xs uppercase tracking-[0.18em] text-primary font-ui mb-1">
                Issue {currentCover.issueNumber}
              </p>
              {currentCover.title && (
                <h3 className="font-display text-lg font-bold text-white">
                  {currentCover.title}
                </h3>
              )}
              {currentCover.tagline && (
                <p className="text-sm text-white/80 mt-1">
                  {currentCover.tagline}
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </Link>

      {/* Navigation Buttons */}
      {covers.length > 1 && (
        <>
          <Button
            data-testid="cover-prev-button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              goToPrev();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            data-testid="cover-next-button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              goToNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </>
      )}

      {/* Dots indicator */}
      {covers.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 pointer-events-none">
          {covers.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-primary w-6' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};